<template>
  <div class="form-group">
    <label class="form-label"
           :data-content="label"
           :class="{ 'd-block': labelline }">
      <span class="">{{ label }}</span>
      <span v-if="required"> *</span>
    </label>
    <div v-if="modelValue && !fileremoved && !selected" class="position-relative d-inline-block">
      <div class="img-preview ml-2"
           v-if="filetype === 'image'">
        <expandable-image class="img-preview"
                          alt="image"
                          :src="modelValue"/>
      </div>
      <div v-if="filetype === 'word'">
        <a class="text-primary ml-3 pointer"
           @click="linkToDoc">
          {{ modelValue }}
        </a>
      </div>
      <div v-if="filetype === 'pdf'">
        <a class="text-primary ml-3 pointer"
           @click="linkDirect">
          {{ modelValue }}
        </a>
      </div>
      <div v-if="filetype === 'document_image'">
        <a class="text-primary ml-3 pointer"
           @click="linkToDocImage">
          {{ modelValue }}
        </a>
      </div>
      <a class="removeFile"
         @click.prevent="deleteFile"
         v-if="deletefile">
        <CloseSvg/>
      </a>
    </div>
    <div v-else>
      <div v-if="readonly">-</div>
      <label class="file-label d-md-flex d-block text-center align-items-center justify-content-center position-relative"
             :class="{ 'bg-light': draggedOver }"
             v-if="!readonly"
             @dragover.prevent="draggedOver = true"
             @dragleave.prevent="draggedOver = false"
             @drop.stop.prevent="dropFile"
             :for="fid">
        <template v-if="selected">

          <div v-if="filedata.length > 10 || webkitdirectory">{{ $t('files') }}: {{ filedata.length }}</div>

          <template v-else-if="filetype == 'image'" >
            <img v-for="(img, imgind) in filedata"
                 :key="`img-${imgind}`"
                 :src="img"
                 class="img-preview mx-1"/>
          </template>

          <div v-else>
            <div v-for="(name, nameind) in names" :key="`name-${nameind}`">
              {{ name }}
            </div>
          </div>
          <!--          <span v-if="files.length > 1" class="ml-2">+ {{ files.length - 1 }}</span>-->
          <a class="removeFile" @click.prevent="deleteFile">
            <CloseSvg/>
          </a>
        </template>
        <template v-else>
          <div v-if="!webkitdirectory">
            <div class="mr-md-4 mt-md-0 mt-3 d-md-inline-block d-block mb-2 mb-md-0">
              <ImgSvg class="file-upload-svg"/>
            </div>
            <div class="d-md-inline-block d-block" v-if="!webkitdirectory">
              <div>{{ $t('drag_file_here_or_select') }}</div>
              <div class=" text-muted">{{ $t('requirements') }}: {{ requirements }}, {{ $t('less') }} {{ sizelimit }} Mb</div>
            </div>
          </div>
          <div v-if="webkitdirectory">
            <div class="mr-md-4 mt-md-0 mt-3 d-md-inline-block d-block mb-2 mb-md-0">
              <FolderSvg class="folder-upload-svg"/>
            </div>
            <div class="d-md-inline-block d-block">
              <div>{{ $t('select_folder') }}</div>
            </div>
          </div>

        </template>
      </label>
      <input type="file"
             :accept="accept"
             class="d-none"
             ref="fileInput"
             :multiple="multiple || webkitdirectory"
             :webkitdirectory="webkitdirectory"
             :id="fid"
             @change="processFile($event)"/>
    </div>
  </div>
</template>

<script setup>
import CloseSvg from "@/assets/svg-vue/general/close.svg"
import ImgSvg from "@/assets/svg-vue/form/file_upload.svg"
import FolderSvg from "@/assets/svg-vue/form/folder_upload.svg"
import JwtService from "@/services/jwt.service"
import {computed, getCurrentInstance, ref} from "vue"
import {useStore} from "vuex"
import i18n from "@/lang/i18n"

const emit = defineEmits(['update:modelValue', 'deleteFile'])

const props = defineProps({
  label: {},
  deletefile: {
    type: Boolean,
    default: false
  },
  labelline: {
    type: Boolean,
    default: false
  },
  modelValue: {},
  filetype: {
    type: String,
    required: true
  },
  sizelimit: { // in MB
    type: Number,
    default: 2
  },
  multiple: {
    type: Boolean,
    default: false
  },
  webkitdirectory: {
    type: Boolean,
    default: false
  },
  fid: {},
  readonly: {},
  required: {
    type: Boolean,
    default: false
  },
})

const store = useStore()
const instance = getCurrentInstance()

const fileInput = ref(null)
const names = ref([props.label])
const selected = ref(false)
const fileremoved = ref(false)
const filedata = ref([])
const files = ref([])
const draggedOver = ref(false)

const clinic = computed(() => store.state.auth.clinic)
const superadmin = computed(() => store.state.auth.superadmin)

const accept = computed(() => {
  if(props.filetype === 'image') {
    return 'image/*'
  }
  if(props.filetype === 'document') {
    return 'application/pdf, .txt, .doc, .docx, .xls, .xlsx'
  }
  if(props.filetype === 'document_image') {
    return 'application/pdf, .txt, .doc, .docx, .xls, .xlsx, image/*'
  }
  if(props.filetype === 'graph_document') {
    return 'image/*, application/pdf'
  }
  if(props.filetype === 'pdf') {
    return 'application/pdf'
  }
  if(props.filetype === 'word') {
    return '.docx'
  }
  if(props.filetype === 'excel') {
    return '.xls, .xlsx'
  }
  if(props.filetype === 'video') {
    return 'video/mp4, video/3gpp'
  }
  return '*/*'
})

const requirements = computed(() => {
  if(accept.value == 'image/*') {
    return 'jpeg, png, tiff, bmp'
  }
  if(props.filetype === 'document') {
    return 'pdf, txt, doc, docx, xls, xlsx'
  }
  if(props.filetype === 'document_image') {
    return 'pdf, txt, doc, docx, xls, xlsx, jpeg, png, tiff, bmp'
  }
  if(props.filetype === 'graph_document') {
    return 'jpeg, png, tiff, bmp, pdf'
  }
  if(props.filetype === 'pdf') {
    return 'pdf'
  }
  if(props.filetype === 'word') {
    return 'docx'
  }
  if(props.filetype === 'video') {
    return 'mp4, 3gp'
  }
  return accept.value
})

const dropFile = (event) => {
  draggedOver.value = false
  processFile({
    target: {
      files: event.dataTransfer.files
    }
  })
}

const processFile = (event) => {
  if(names.value.length > 0 && names.value[0] == props.label) names.value.shift()
  if(props.multiple || props.webkitdirectory) {
    let eventFiles = event.target.files
    let data = props.modelValue || []
    eventFiles.forEach(file => {
      let f = handleFile(file)
      if(f) data.push(f)
    })
    emit('update:modelValue', data)
  }
  if(!(props.multiple || props.webkitdirectory)) {
    let file = event.target.files[0]
    if(!file) {
      return
    }
    let f = handleFile(file)
    if(f) emit('update:modelValue', f)
  }
  files.value = event.target.files
}

const handleFile = (file) => {
  if(file.size > (props.sizelimit * 1024 * 1024)) {
    instance.proxy.$noty.error(i18n.global.t('file_size_is_too_big'))
    return
  }
  names.value.push(file.name)

  selected.value = true

  if(props.filetype === 'image') {
    let reader = new FileReader()
    reader.onload = function(e) {
      filedata.value.push(e.target.result)
    }
    reader.readAsDataURL(file)
  }

  return file
}

const deleteFile = () => {
  fileremoved.value = true
  selected.value = false
  filedata.value = []
  names.value = []
  emit('update:modelValue', props.multiple ? [] : null)
  emit('deleteFile')
}

const linkToDoc = () => {
  let link = JwtService.getPrintPrelink(clinic.value, superadmin.value)
  link = link+'/template/document/'+store.state.datatable.selectedModel.id
  if(superadmin.value) {
    link = link + `?superadmin_clinic_id=${clinic.value?.id || ''}`
  }
  window.open(link, '_blank')
}

const linkToDocImage = () => {
  let link = JwtService.getPrintPrelink(clinic.value, superadmin.value)
  link = link+'/file/notification_settings/'+store.state.datatable.selectedModel.id+'/'+props.modelValue
  if(superadmin.value) {
    link = link + `?superadmin_clinic_id=${clinic.value?.id || ''}`
  }
  window.open(link, '_blank')
}

const linkDirect = () => {
  window.open(props.modelValue, '_blank')
}

// called by parents via ref (PatientAnketaDropdown)
const triggerFileInput = () => {
  fileInput.value.click()
}

defineExpose({
  triggerFileInput,
})
</script>

<style scoped lang="scss">
.file-btn {
  &.selected {
    background: #000;
    color: #fff;
  }
}
.img-preview:not(.expanded) {
  max-width: 60px;
  img {
    max-width: 60px;
  }
}
.removeFile {
  position: absolute;
  top: -10px;
  right: -10px;
  box-shadow: 0px 0px 5px rgba(146, 169, 176, 0.3);
  border-radius: 100%;
  background: #fff;
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 10px;
    height: 10px;
    fill: #000;
  }
}
.file-label {
  cursor: pointer;
  background: #FFFDFD;
  border-radius: 5px;
  border: 1px dashed $gray-icon;
  width: 100%;
  height: 80px;
  font-size: 12px;
  &:hover {
    background: #fff;
  }
}
.file-upload-svg {
  width: 52px;
  height: 52px;
}
.folder-upload-svg {
  width: 44px;
  height: 44px;
}

@media screen and (max-width: 768px) {
  .file-label {
    height: auto;
    min-height: 110px;
  }
  .file-upload-svg {
    width: 40px;
    height: 40px;
  }
}

</style>
